import { useRequiredContext } from "@redotech/react-util/context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { memo } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { ConversionSuiteStatusesContext } from "../../redo-suite/conversion-cloud";

/**
 * Route-level guard for direct-URL access to gated Conversion Cloud features
 * (CRO-1387). Renders the matching child route only when the team has access:
 * - intent-on teams (intent-only / intent-only-small) are redirected to the
 *   Conversion dashboard (where the upsell pitch lives), UNLESS the route is
 *   one they own — pass `allowIntent` for those (e.g. Spotlights), and the
 *   route renders instead.
 * - intent-off teams (intent-rescue-small / none) are redirected to the
 *   welcome page; they have no secondary nav and shouldn't reach gated routes.
 * - full teams always render.
 *
 * Wrap the parent route of any gated path:
 *
 *   { path: "ab-tests", element: <ConversionFeatureGate />, children: [...] }
 *   { path: "spotlights", element: <ConversionFeatureGate allowIntent />, ... }
 */
export const ConversionFeatureGate = memo(function ConversionFeatureGate({
  allowIntent = false,
}: {
  allowIntent?: boolean;
}) {
  const { accessLevel } = useRequiredContext(ConversionSuiteStatusesContext);
  const routes = useRequiredContext(MerchantRoutesContext);

  if (accessLevel === "intent-only" || accessLevel === "intent-only-small") {
    if (allowIntent) {
      return <Outlet />;
    }
    return <Navigate replace to={routes.getConversionSubRoute("dashboard")} />;
  }
  if (accessLevel === "intent-rescue-small" || accessLevel === "none") {
    return <Navigate replace to={routes.getConversionSubRoute("welcome")} />;
  }

  return <Outlet />;
});
